/**
 * useGames — React hooks for HupperGames-side wallet interactions.
 *
 *   - useUsdcBalance(addr)               → on-chain USDC balance of a wallet
 *   - useGamesAllowance(owner)           → current allowance(owner, HupperGames)
 *   - useGamesActions()                  → approve / send USDC via Privy
 *                                          embedded wallet (or any injected
 *                                          EIP-1193 provider)
 *
 * USDC stays in the user's own wallet at all times. The platform never
 * holds the user's money: lock / settle / transfer happen via the
 * SETTLER_ROLE key on the server side, pulling from the user via the
 * one-time approval done here.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Contract,
  type ContractRunner,
  type Eip1193Provider,
  JsonRpcProvider,
  BrowserProvider,
  MaxUint256,
} from "ethers";
import { useWallets } from "@privy-io/react-auth";
import MockUSDTAbi from "../contracts/MockUSDTAbi.json";
import { ACTIVE_CHAIN, isGamesDeployed } from "../contracts/chains";

interface BalanceState {
  loading: boolean;
  value: bigint;
  error: Error | null;
}
const ZERO: BalanceState = { loading: false, value: 0n, error: null };

function readProvider(): JsonRpcProvider {
  return new JsonRpcProvider(ACTIVE_CHAIN.rpcUrl, ACTIVE_CHAIN.chainId);
}

function usdcContract(runner: ContractRunner): Contract {
  return new Contract(ACTIVE_CHAIN.usdcAddress!, MockUSDTAbi, runner);
}

/** Wallet-held USDC balance (the user's own wallet — what they can spend). */
export function useUsdcBalance(
  user: string | null,
  pollIntervalMs = 12_000,
): BalanceState & { refetch: () => Promise<void> } {
  const [state, setState] = useState<BalanceState>({ ...ZERO, loading: !!user });

  const fetch = useCallback(async () => {
    if (!user || !ACTIVE_CHAIN.usdcAddress) {
      setState(ZERO);
      return;
    }
    try {
      const c = usdcContract(readProvider());
      const value: bigint = await c.balanceOf(user);
      setState({ loading: false, value, error: null });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setState({ loading: false, value: 0n, error });
    }
  }, [user]);

  useEffect(() => {
    fetch();
    if (!user || pollIntervalMs <= 0) return;
    const id = setInterval(fetch, pollIntervalMs);
    return () => clearInterval(id);
  }, [fetch, user, pollIntervalMs]);

  return { ...state, refetch: fetch };
}

/** allowance(owner, HupperGames). Used to gate the "Authorize Hupper" CTA. */
export function useGamesAllowance(
  owner: string | null,
  pollIntervalMs = 30_000,
): BalanceState & { refetch: () => Promise<void> } {
  const [state, setState] = useState<BalanceState>({ ...ZERO, loading: !!owner });

  const fetch = useCallback(async () => {
    if (!owner || !isGamesDeployed()) {
      setState(ZERO);
      return;
    }
    try {
      const c = usdcContract(readProvider());
      const value: bigint = await c.allowance(owner, ACTIVE_CHAIN.gamesAddress!);
      setState({ loading: false, value, error: null });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setState({ loading: false, value: 0n, error });
    }
  }, [owner]);

  useEffect(() => {
    fetch();
    if (!owner || pollIntervalMs <= 0) return;
    const id = setInterval(fetch, pollIntervalMs);
    return () => clearInterval(id);
  }, [fetch, owner, pollIntervalMs]);

  return { ...state, refetch: fetch };
}

interface ActionState {
  pending: boolean;
  error: Error | null;
  txHash: string | null;
}
const ACTION_IDLE: ActionState = { pending: false, error: null, txHash: null };

/**
 * Actions on USDC from the user's wallet:
 *   - approveGames(amount): user → HupperGames allowance. Default amount =
 *     MaxUint256 ("infinite"). Done once per user.
 *   - sendUsdc(to, amount): plain ERC20 transfer out of the user's wallet.
 *     This is how the user "withdraws" — straight from their wallet to any
 *     destination. There is no Hupper-held balance to withdraw.
 *
 * Uses the Privy embedded wallet by preference, falls back to window.ethereum.
 */
export function useGamesActions() {
  const [status, setStatus] = useState<ActionState>(ACTION_IDLE);
  const { wallets } = useWallets();

  const resolveProvider = useCallback(async (): Promise<Eip1193Provider | null> => {
    const embedded = wallets.find((w) => w.walletClientType === "privy");
    const wallet = embedded ?? wallets[0];
    if (wallet) {
      try {
        await wallet.switchChain(ACTIVE_CHAIN.chainId);
      } catch {
        // chain may already be active or wallet may not support this; continue
      }
      return (await wallet.getEthereumProvider()) as unknown as Eip1193Provider;
    }
    if (typeof window === "undefined") return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (window as any).ethereum ?? null;
  }, [wallets]);

  const runWrite = useCallback(
    async (label: string, fn: (c: Contract) => Promise<{ hash: string; wait: () => Promise<unknown> }>) => {
      if (!isGamesDeployed()) {
        const err = new Error(`HupperGames not deployed on ${ACTIVE_CHAIN.name}`);
        setStatus({ pending: false, error: err, txHash: null });
        throw err;
      }
      const provider = await resolveProvider();
      if (!provider) {
        const err = new Error("No wallet available (login first or install MetaMask)");
        setStatus({ pending: false, error: err, txHash: null });
        throw err;
      }
      setStatus({ pending: true, error: null, txHash: null });
      try {
        const bp = new BrowserProvider(provider, ACTIVE_CHAIN.chainId);
        const signer = await bp.getSigner();
        const tx = await fn(usdcContract(signer));
        const txHash = tx.hash;
        await tx.wait();
        setStatus({ pending: false, error: null, txHash });
        return { hash: txHash };
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setStatus({ pending: false, error, txHash: null });
        // eslint-disable-next-line no-console
        console.error(`[useGamesActions:${label}]`, error);
        throw error;
      }
    },
    [resolveProvider],
  );

  const approveGames = useCallback(
    (amount: bigint = MaxUint256) =>
      runWrite("approveGames", (c) => c.approve(ACTIVE_CHAIN.gamesAddress!, amount)),
    [runWrite],
  );

  const sendUsdc = useCallback(
    (to: string, amount: bigint) => runWrite("sendUsdc", (c) => c.transfer(to, amount)),
    [runWrite],
  );

  return { approveGames, sendUsdc, status };
}

/** Format a USDC amount (6 decimals) to a 2-decimal string. */
export function formatUSDC(amount: bigint): string {
  const whole = amount / 1_000_000n;
  const frac = amount % 1_000_000n;
  const fracStr = frac.toString().padStart(6, "0").slice(0, 2);
  return `${whole}.${fracStr}`;
}

/** Parse a human USDC amount (1.50) to base units (1500000n). */
export function parseUSDC(amount: number | string): bigint {
  const n = typeof amount === "string" ? parseFloat(amount) : amount;
  if (!Number.isFinite(n) || n < 0) throw new Error(`Invalid USDC amount: ${amount}`);
  return BigInt(Math.round(n * 1_000_000));
}
